import React from 'react';
import { marked } from 'marked';
import parse from 'html-react-parser';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// コードブロック用のコンポーネント
const CodeBlock = ({ className = '', children = [] }) => {
  const match = /language-(\w+)/.exec(className || '');
  const language = match?.[1] || '';

  const code = typeof children === 'string' ? children : children.join('');

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
    } catch (err) {
      console.error('Copy failed', err);
    }
  };

  return (
    <div style={{ position: 'relative', marginTop: '1em' }}>
      <button
        onClick={handleCopy}
        style={{
          position: 'absolute',
          top: '6px',
          right: '10px',
          zIndex: 1,
          background: '#444',
          color: '#fff',
          border: 'none',
          borderRadius: '4px',
          padding: '2px 6px',
          fontSize: '12px',
          cursor: 'pointer',
        }}
      >
        Copy
      </button>
      <SyntaxHighlighter language={language} style={oneDark} PreTag="div">
        {code.trim()}
      </SyntaxHighlighter>
    </div>
  );
};

// 改行文字（"\n"）を再帰的に削除する
const removeNewlineTextNodes = (node) => {
  if (Array.isArray(node)) {
    return node.map(removeNewlineTextNodes).filter((n) => n !== '\n');
  }
  if (typeof node === 'object' && node !== null && node.props) {
    const children = node.props.children;
    return {
      ...node,
      props: {
        ...node.props,
        children: removeNewlineTextNodes(children),
      },
    };
  }
  return node;
};

const MarkdownRenderer = ({ text }) => {
  if (!text) return null;

  // marked を使って HTML に変換
  marked.setOptions({
    breaks: false,
    gfm: true,
    highlight: null,
  });

  const rawHtml = marked.parse(text);

  // HTML を React に変換
  const parsedHtml = parse(rawHtml, {
    replace: (domNode) => {
      if (domNode.name === 'code' && domNode.parent?.name === 'pre') {
        return (
          <CodeBlock className={domNode.attribs.class}>
            {domNode.children[0]?.data || ''}
          </CodeBlock>
        );
      }
      // Base64画像表示対応
      if (domNode.name === 'img' && domNode.attribs?.src?.startsWith('data:image')) {
        return (
          <img
            src={domNode.attribs.src}
            alt={domNode.attribs.alt || 'uploaded image'}
            style={{
              maxWidth: '100%',
              maxHeight: '300px',
              marginTop: '5px',
              borderRadius: '8px',
            }}
          />
        );
      }
      // リンクを新規タブで開く
      if (domNode.name === 'a' && domNode.attribs?.href) {
        return (
          <a
            href={domNode.attribs.href}
            target="_blank"
            rel="noopener noreferrer"
            title={domNode.attribs.title}
          >
            {domNode.children.map((child, i) => 
              child.type === 'text' ? child.data : null
            )}
          </a>
        );
      }
    },
  });

  const cleaned = removeNewlineTextNodes(parsedHtml);

  return <div>{cleaned}</div>;
};

export default MarkdownRenderer;
